import React, { useMemo, useState } from "react";
import { Alert, Box, Typography } from "@mui/material";
import {
  Bar, CartesianGrid, ComposedChart, LabelList,
  ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";

import {
  getPaymentsOpsSummary, getPaymentsOpsTrend, getPaymentsOpsDetail,
} from "../../services/ops/opsservices";
import { useClientTimeZone, zoneAbbr } from "../../utils/clienttimezone";
import ConversationDialog from "./ConversationDialog";
import {
  BLUE, GREEN,
  ChartCard, SectionTitle, Caption, DetailTable, LoadingRow,
  OpsSlicers, StatusChip, identityColumns, drillColumn, useOpsData,
  DEFAULT_GRAN, DEFAULT_RANGE, getDateRange,
  formatRangeLabel,
  fmtInt, fmtAxisDate, fmtTime, safeNum,
} from "./opsShared";

// Ledger TotalAmount: what the card was charged, convenience fee included —
// the same "total = $X" the customer's confirmation quotes.
//
// Compact, one decimal — the same format as the PTP tab's KPI tiles, so the
// two pages print the same figure the same way: $13.7K, $1.25M, $535.
// Switches to M only once the K form would need four digits (1000.0K+).
function fmtUSD(v) {
  const num = safeNum(v);
  if (!num) return "$0";
  if (Math.abs(Number((num / 1000).toFixed(1))) >= 1000)
    return `$${(num / 1_000_000).toFixed(2)}M`;
  if (Math.abs(num) >= 1000) return `$${(num / 1000).toFixed(1)}K`;
  return `$${num.toFixed(0)}`;
}

// The total tile is neither Completed nor Scheduled, so it takes a neutral ink
// rather than borrowing either hue.
const NAVY = "#0C447C";

// ── LANGUAGE SPLIT ────────────────────────────────────────────────
// Language comes from the campaign's [Language] tag, resolved server-side to
// EN / SP / Other. Other is anything untagged; it is only drawn when non-zero,
// so on a fully tagged book the tile shows EN and SP and they sum to 100%.
//
// The % is SHARE OF COUNT — of the payments in this tile, how many came from
// Spanish campaigns. The $ beside it is that language's own total, so a
// language with fewer but larger payments reads correctly as both.
const LANGS = [
  { key: "En",    label: "EN" },
  { key: "Sp",    label: "SP" },
  { key: "Other", label: "Other", hideIfZero: true },
];

// Pull { count, amount } per language for one event type from the summary row.
function langSplit(s, type) {
  return LANGS.map((l) => ({
    ...l,
    count:  safeNum(s[`${type}${l.key}Count`]),
    amount: safeNum(s[`${type}${l.key}Amount`]),
  }));
}

// Element-wise sum of two splits — the PTP tile (Same Day + Future Payments).
function addSplits(a, b) {
  return a.map((row, i) => ({
    ...row,
    count:  row.count  + b[i].count,
    amount: row.amount + b[i].amount,
  }));
}

/**
 * KPI tile with a total on top and a language breakdown underneath.
 *
 *   SAME DAY
 *   128              $41,230.55
 *   ─────────────────────────────
 *   EN   82%    105    $33,410.20
 *   SP   18%     23     $7,820.35
 *
 * Local rather than in opsShared: no other Ops page has a language dimension,
 * and CounterCard has no slot for rows.
 */
// `ofTotal` (optional) is the PTP count this tile is a part of. When given,
// the headline gets its % of PTP beside it — Same Day and Future Payments each
// as a share of PTP by count, so the two always add to 100%.
function LangKpiCard({ label, color, count, amount, split, ofTotal }) {
  const rows = split.filter((r) => !(r.hideIfZero && r.count === 0));
  // Type scale and spacing match LangKpiTile on the Texts and PTP tabs exactly:
  // 11px sentence-case label, 22px / 500 headline, 13px aside, 11.5px rows.
  return (
    <Box sx={{
      background: "var(--mui-palette-background-default, #f7f7f7)",
      borderRadius: 2, p: "10px 14px", minWidth: 0,
      display: "flex", flexDirection: "column",
    }}>
      <Typography sx={{
        fontSize: 11, color: "text.secondary", mb: 0.5,
        whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
      }}>
        {label}
      </Typography>

      <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 1 }}>
        <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.75, minWidth: 0 }}>
          <Typography sx={{ fontSize: 22, fontWeight: 500, color, lineHeight: 1.2, whiteSpace: "nowrap" }}>
            {fmtInt(count)}
          </Typography>
          {ofTotal != null && (
            <Typography sx={{ fontSize: 12, fontWeight: 600, color, whiteSpace: "nowrap" }}>
              {ofTotal > 0 ? `${((count / ofTotal) * 100).toFixed(1)}%` : "—"}
            </Typography>
          )}
        </Box>
        <Typography sx={{ fontSize: 13, fontWeight: 500, color: "text.primary", whiteSpace: "nowrap" }}>
          {fmtUSD(amount)}
        </Typography>
      </Box>

      <Box sx={{ borderTop: "0.5px solid", borderColor: "divider", mt: 0.75, pt: 0.5 }}>
        {rows.map((r) => {
          const pct = count > 0 ? (r.count / count) * 100 : null;
          const muted = r.key === "Other";
          return (
            <Box key={r.key} sx={{
              display: "grid", gridTemplateColumns: "30px 48px 1fr auto",
              alignItems: "center", columnGap: 0.75, fontSize: 11.5, py: "1px",
              color: muted ? "text.disabled" : "text.secondary",
            }}>
              <Box sx={{ fontWeight: 600 }}>{r.label}</Box>
              <Box sx={{ fontWeight: 600, color: muted ? "inherit" : color }}>
                {pct == null ? "—" : `${pct.toFixed(1)}%`}
              </Box>
              <Box sx={{ textAlign: "right" }}>{fmtInt(r.count)}</Box>
              <Box sx={{ textAlign: "right", minWidth: 52 }}>{fmtUSD(r.amount)}</Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

export default function PaymentActivityDashboard() {
  const [gran,  setGran]  = useState(DEFAULT_GRAN);
  const [range, setRange] = useState(DEFAULT_RANGE);

  const { timeZone, tzReady } = useClientTimeZone();
  const tzLabel = zoneAbbr(timeZone);

  const params = useMemo(() => ({
    ...getDateRange(range, timeZone),
    granularity: gran.toLowerCase(),
  }), [range, gran, timeZone]);

  // Any account row opens its full conversation.
  const [drill, setDrill] = useState(null);
  const openThread = (row) => setDrill(row);

  const { data, loading, error } = useOpsData({
    summary: getPaymentsOpsSummary,
    trend:   getPaymentsOpsTrend,
    detail:  getPaymentsOpsDetail,
  }, params, tzReady);

  const s = data.summary || {};
  const completed    = safeNum(s.paymentsCompleted);
  const scheduled    = safeNum(s.paymentsScheduled);
  const completedAmt = safeNum(s.amountCompleted);
  const scheduledAmt = safeNum(s.amountScheduled);

  const completedSplit = langSplit(s, "completed");
  const scheduledSplit = langSplit(s, "scheduled");
  const allSplit       = addSplits(completedSplit, scheduledSplit);

  // No tile: a counter pinned at 0 is permanent noise on a page people open to
  // read payment volume. Kept as a value because the banner below it — which
  // only appears when the count is non-zero — is the part worth seeing, and
  // Duplicate Watch is where it gets worked.
  const repeats   = safeNum(s.paymentsWithRepeatDelivery);

  const trend = (data.trend || []).map((r) => ({
    date:      fmtAxisDate(r.summary_date, gran, timeZone),
    completed: safeNum(r.paymentsCompleted),
    scheduled: safeNum(r.paymentsScheduled),
  }));

  const rangeLabel = formatRangeLabel(params, timeZone);
  const titleOf = (t) => `${t} · ${rangeLabel}`;

  const columns = [
    drillColumn(openThread),
    ...identityColumns,
    { key: "paymentType", label: "Type", width: 100,
      render: (r) => (
        <StatusChip label={r.paymentType} tone={r.paymentType === "Completed" ? "ok" : "default"} />
      ) },
    { key: "customerMessage", label: "Customer's Message", width: 180 },
    { key: "deliveryAttempts", label: "Attempts", width: 90, align: "right",
      render: (r) => {
        const n = safeNum(r.deliveryAttempts);
        return n > 1 ? <StatusChip label={String(n)} tone="bad" /> : n;
      } },
    { key: "receivedUtc",  label: "Received",  width: 160, render: (r) => fmtTime(r.receivedUtc, timeZone) },
    { key: "confirmedUtc", label: "Confirmed", width: 160, render: (r) => fmtTime(r.confirmedUtc, timeZone) },
    { key: "confirmation", label: "Confirmation", width: 360 },
  ];

  return (
    <Box sx={{ p: 2 }}>
      <OpsSlicers gran={gran} setGran={setGran} range={range} setRange={setRange} tzLabel={tzLabel} />

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && <LoadingRow text="Counting payment activity…" />}

      {!loading && (
        <>
          <SectionTitle>Payment Activity</SectionTitle>
          {/* Three tiles: PTP first, then its two parts — the same order as the
              PTP tab's KPI row (PTP, then Same Day). Each carries its total and
              the EN / SP split by share of count, count and $. Three columns
              rather than four because the language rows need the width; capped
              so the tiles do not stretch into banners on a wide screen. */}
          <Box sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0,1fr))" },
            gap: 1, mb: 1.5, maxWidth: 1100,
          }}>
            <LangKpiCard label="PTP" color={NAVY}
              count={completed + scheduled} amount={completedAmt + scheduledAmt} split={allSplit} />
            <LangKpiCard label="Same Day" color={GREEN}
              count={completed} amount={completedAmt} split={completedSplit}
              ofTotal={completed + scheduled} />
            <LangKpiCard label="Future Payments" color={BLUE}
              count={scheduled} amount={scheduledAmt} split={scheduledSplit}
              ofTotal={completed + scheduled} />
          </Box>

          {repeats > 0 && (
            <Alert severity="error" sx={{ mb: 2, fontSize: 13 }}>
              {repeats} payment {repeats === 1 ? "confirmation was" : "confirmations were"}
              {" "}produced on a message that reached Vera more than once. The Duplicate Watch
              tab lists them with the account details needed to check the gateway.
            </Alert>
          )}

          <Box sx={{ mb: 1.5 }}>
            <ChartCard title={titleOf("Payments over time")} height={300}>
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={trend} margin={{ top: 18, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" />
                  <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#888" }} interval="preserveStartEnd" minTickGap={24} />
                  <YAxis tick={{ fontSize: 10, fill: "#888" }} allowDecimals={false} />
                  <Tooltip contentStyle={{ fontSize: 12 }} />
                  {/* Completed and Scheduled stack because together they are
                      "payment secured" — one arrived, the other is committed. */}
                  <Bar dataKey="completed" name="Completed" stackId="p" fill={GREEN} />
                  <Bar dataKey="scheduled" name="Scheduled" stackId="p" fill={BLUE} radius={[3, 3, 0, 0]}>
                    <LabelList dataKey="scheduled" position="top" fontSize={10} fill={BLUE}
                      formatter={(v) => (v > 0 ? v : "")} />
                  </Bar>
                </ComposedChart>
              </ResponsiveContainer>
            </ChartCard>
            <Caption>
              Counts and amounts of payment events on the transaction ledger (CF-AI3-PAY
              completed, CF-AI4-PTP scheduled), in any language. Amounts are the total
              charged, including the convenience fee. EN / SP come from the campaign's
              language tag, and the % is each language's share of the payment count;
              "Other" appears only for payments on an untagged campaign. A row marked
              "confirmation not sent to customer" settled without the customer being
              told; those are also listed on Unconfirmed Payments.
            </Caption>
          </Box>

          <SectionTitle right={`${(data.detail || []).length} shown`}>
            Payment Confirmations
          </SectionTitle>
          <DetailTable
            columns={columns}
            rows={data.detail || []}
            emptyText="No payments in this range."
            maxHeight={480}
          />
        </>
      )}

      <ConversationDialog
        open={!!drill}
        onClose={() => setDrill(null)}
        customerdataid={drill?.customerdataid}
      />
    </Box>
  );
}